<template>
  <div class="relative min-h-screen font-sans smooth-scroll">
    <div class="grain-overlay" aria-hidden="true"></div>

    <!-- ── PREMIUM PILL NAVBAR ───────────────────────────────── -->
    <div class="navbar-wrapper">
      <nav class="navbar" :class="{ 'navbar--scrolled': hasScrolled }">
        <div class="navbar__inner">
          
          <!-- Logo section -->
          <router-link to="/" class="navbar__logo">
            <img src="./assets/logo-beli.png" class="navbar__logo-img" alt="Logo">
          </router-link>

          <!-- Navigation Links (Hidden on Mobile) -->
          <ul class="navbar__links">
            <template v-if="authData.authenticated && !isPublicBookingRoute">
              <li>
                <router-link v-if="$route.name !== 'dashboard'" to="/dashboard" class="navbar__link">
                  <i class="mr-1 text-blue-400 fa fa-chart-line"></i> {{ $t('dashboard') || 'Dashboard' }}
                </router-link>
              </li>
              <li>
                <router-link v-if="$route.name !== 'edit'" to="/edit" class="navbar__link">
                  <i class="mr-1 fa fa-edit text-emerald-400"></i> {{ $t('edit') || 'Edit' }}
                </router-link>
              </li>
            </template>
          </ul>

          <!-- Right Actions -->
          <div class="navbar__right">
            
            <!-- Customer profile icon -->
            <button v-if="authData.authenticated && isCustomer" @click="isCustomerDrawerOpen = true" class="btn-profile-icon" aria-label="My account">
              <i class="fa fa-user-circle"></i>
            </button>

            <!-- Language Dropdown -->
            <div class="lang-picker language-dropdown-container" ref="langPicker">
              <button @click.stop="toggleLanguageDropdown" class="lang-btn">
                <span class="mr-1 drop-shadow-sm">{{ flags[currentLanguage] || '🌐' }}</span>
                <span class="font-semibold tracking-wide lang-text">{{ currentLanguage }}</span>
                <i class="ml-1 text-[10px] fas fa-chevron-down transition-transform" :class="{ 'rotate-180': showLanguageDropdown }"></i>
              </button>
              
              <transition name="drop">
                <div v-if="showLanguageDropdown" class="lang-dropdown">
                  <button
                    v-for="lang in availableLanguages" :key="lang"
                    @click="changeLanguage(lang)"
                    class="lang-option" :class="{ 'is-active': currentLanguage === lang }"
                  >
                    <span class="mr-2 text-lg transition-transform drop-shadow-md hover:scale-110">{{ flags[lang] }}</span> 
                    {{ lang }}
                    <i v-if="currentLanguage === lang" class="ml-auto text-blue-500 fas fa-check"></i>
                  </button>
                </div>
              </transition>
            </div>

            <!-- Logout Button (Hidden on Mobile) -->
            <template v-if="authData.authenticated && !isPublicBookingRoute">
              <div class="nav-divider desktop-only"></div>
              <button @click="logout" class="dash-btn btn-danger-outline desktop-only py-1.5 px-3 text-xs ml-1">
                <i class="fa fa-sign-out-alt"></i> {{ $t('logout') || 'Logout' }}
              </button>
            </template>

          </div>
        </div>
      </nav>
    </div>

    <!-- ── MAIN CONTENT ──────────────────────────────────────── -->
    <main class="pt-28 min-h-[calc(100vh-80px)] pb-24 sm:pb-8">
      <div class="mx-auto max-w-7xl sm:px-6 lg:px-8">
        <router-view v-slot="{ Component, route }">
          <transition name="page-fade" mode="out-in">
            <div :key="route.path" class="px-4 py-6 sm:px-0">
              <component :is="Component" />
            </div>
          </transition>
        </router-view>
      </div>
    </main>

    <!-- ── FOOTER ────────────────────────────────────────────── -->
    <footer class="site-footer">
      <div class="footer-inner">
        <div class="footer-grid">
          <div>
            <img src="./assets/logo-beli.png" class="footer-logo" alt="Barbers Unity">
            <p class="footer-desc">{{ $t('footer.description') }}</p>
          </div>
          <div>
            <p class="footer-heading">{{ $t('footer.quickLinks.title') }}</p>
            <router-link to="/about" class="footer-link">{{ $t('footer.quickLinks.about') }}</router-link>
            <router-link to="/contact" class="footer-link">{{ $t('footer.quickLinks.contact') }}</router-link>
            <a href="#" class="footer-link">{{ $t('footer.quickLinks.demo') }}</a>
          </div>
          <div>
            <p class="footer-heading">{{ $t('footer.legal.title') }}</p>
            <router-link to="/privacy" class="footer-link">{{ $t('footer.legal.privacy') }}</router-link>
            <router-link to="/terms" class="footer-link">{{ $t('footer.legal.terms') }}</router-link>
          </div>
          <div>
            <p class="footer-heading">{{ $t('footer.followUs') }}</p>
            <div class="footer-social">
              <a href="https://www.facebook.com/profile.php?id=61575548669877"><i class="fab fa-facebook"></i></a>
              <a href="https://www.instagram.com/withbarbersunity/"><i class="fab fa-instagram"></i></a>
              <a href="https://www.tiktok.com/@barbersunity"><i class="fa-brands fa-tiktok"></i></a>
            </div>
          </div>
        </div>
        <div class="footer-bottom">
          <p>{{ $t('footer.copyright') }}</p>
        </div>
      </div>
    </footer>

    <!-- ── COOKIE CONSENT ────────────────────────────────────── -->
    <transition enter-active-class="transition duration-500 ease-out" enter-from-class="transform translate-y-full opacity-0" enter-to-class="transform translate-y-0 opacity-100" leave-active-class="transition duration-300 ease-in" leave-from-class="transform translate-y-0 opacity-100" leave-to-class="transform translate-y-full opacity-0">
      <div v-if="showCookieConsent" class="cookie-bar">
        <div class="cookie-inner">
          <div class="cookie-text">
            <strong>{{ $t('cookies.title') || 'Cookie Preferences' }}</strong>
            <span>{{ $t('cookies.message') || 'We use cookies to enhance your experience.' }} <router-link to="/privacy">{{ $t('footer.legal.privacy') || 'Learn more' }}</router-link>.</span>
          </div>
          <div class="cookie-actions">
            <button @click="declineCookies" class="cookie-btn cookie-btn--ghost">{{ $t('cookies.decline') || 'Decline' }}</button>
            <button @click="acceptCookies"  class="cookie-btn cookie-btn--solid">{{ $t('cookies.accept') || 'Accept' }}</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ── MOBILE BOTTOM NAVIGATION (B2B) ────────────────────── -->
    <teleport to="body">
      <div v-if="authData.authenticated && !isPublicBookingRoute" class="mobile-bottom-nav desktop-hide">
        <div class="mobile-bottom-nav__inner">
          <router-link to="/dashboard" class="mobile-nav-btn" active-class="is-active">
            <i class="fa fa-chart-line"></i>
            <span>{{ $t('dashboard') || 'Dashboard' }}</span>
          </router-link>
          
          <router-link to="/edit" class="mobile-nav-btn" active-class="is-active">
            <i class="fa fa-edit"></i>
            <span>{{ $t('edit') || 'Edit' }}</span>
          </router-link>
          
          <button @click="logout" class="mobile-nav-btn logout-btn">
            <i class="fa fa-sign-out-alt"></i>
            <span>{{ $t('logout') || 'Logout' }}</span>
          </button>
        </div>
      </div>
    </teleport>

    <CustomerDrawer :isOpen="isCustomerDrawerOpen" @close="isCustomerDrawerOpen = false" />
    <BuToast />
    <div class="noise-overlay"></div>
  </div>
</template>

<script>
import { parseUrlData } from './fun/fun'
import { Auth } from "@/services"
import BuToast from '@/components/BuToast.vue'
import CustomerDrawer from '@/components/CustomerDrawer.vue'

export default {
  name: 'App',
  data() {
    return {
      currentLanguage: localStorage.getItem("language"),
      availableLanguages: ['ENG','BG','ALB', 'HR', 'GER', 'SLO', 'FR'],
      showLanguageDropdown: false,
      hasScrolled: false,
      darkMode: true,
      authData: Auth.state,
      shopName: '',
      showCookieConsent: false,
      flags: { 'ENG': '🇬🇧', 'BG': '🇧🇬', 'ALB': '🇦🇱', 'HR': '🇭🇷', 'GER': '🇩🇪', 'SLO': '🇸🇮', 'FR': '🇫🇷' },
      isCustomerDrawerOpen: false,
    }
  },
  components: {
    BuToast,
    CustomerDrawer
  },
  computed: {
    isPublicBookingRoute() {
      const name = this.$route.name
      return ['home', 'calendar', 'form', 'about', 'privacy', 'terms'].includes(name)
    },
    isCustomer() {
      const user = Auth.getUser();
      return !!(user && user.isCustomer);
    },
    displayShopName() {
      if (['admin', 'superadmin-login', 'superadmin-panel', 'register'].includes(this.$route?.name)) {
        return 'Barbers Unity';
      }
      return this.shopName || 'Barbers Unity';
    }
  },
  methods: {
    toggleLanguageDropdown() {
      this.showLanguageDropdown = !this.showLanguageDropdown;
    },
    changeLanguage(lang) {
      localStorage.setItem("language", lang);
      const { language, partner } = parseUrlData(window.location.href);
      if(partner != null){
        this.currentLanguage = lang;
        this.showLanguageDropdown = false;
        window.location.href = `${window.location.origin}/${lang.toLowerCase()}/${partner}`
      } else {
        location.reload();
      }
    },
    handleClickOutside(event) {
      if (!event.target.closest('.language-dropdown-container')) {
        this.showLanguageDropdown = false;
      }
    },
    closeAllMenus() {
      this.showLanguageDropdown = false;
    },
    handleScroll() {
      this.hasScrolled = window.scrollY > 50;
    },
    logout(){
      Auth.logout();
    },
    setLanguage() {
      try {
        const { language } = parseUrlData(window.location.href);
        let upercaseLang = language?.toUpperCase()
        if (this.currentLanguage === null && !language) {
          localStorage.setItem("language", "ENG");
        } else if (language) {
          localStorage.setItem("language", upercaseLang);
          if(this.currentLanguage != upercaseLang){
            location.reload();
          }
        }
      } catch(e) {
        console.error(e);
      }
    },
    getShopName(){
      try{
        this.shopName = '';
        const data = JSON.parse(localStorage.getItem("data"));
        if (data) {
          const payload = data.payload || data;
          if (Array.isArray(payload) && payload[0]) {
            this.shopName = payload[0].salonName || payload[0].name || '';
          }
          this.updatePageTitle();
        }
      } catch(e) {
        console.error(e);
      }
    },
    checkCookieConsent() {
      const consent = localStorage.getItem('cookieConsent');
      if (!consent) {
        setTimeout(() => { this.showCookieConsent = true }, 1500);
      }
    },
    acceptCookies() {
      localStorage.setItem('cookieConsent', 'accepted');
      this.showCookieConsent = false;
    },
    declineCookies() {
      localStorage.setItem('cookieConsent', 'declined');
      this.showCookieConsent = false;
    },
    updatePageTitle(route = this.$route) {
      const name = route?.name;
      const shop = ['admin', 'superadmin-login', 'superadmin-panel', 'register'].includes(name) ? 'Barbers Unity' : (this.shopName || 'Barbers Unity');
      let pageName = '';

      if (name === 'home') pageName = this.$t('home');
      else if (name === 'about') pageName = this.$t('about');
      else if (name === 'calendar' || name === 'form') pageName = this.$t('bookingForm') || 'Booking';
      else if (name === 'dashboard') pageName = this.$t('dashboard');
      else if (name === 'edit') pageName = this.$t('edit') || 'Edit';
      else if (name === 'admin' || name === 'superadmin-login') pageName = this.$t('login');
      else if (name === 'superadmin-panel') pageName = 'SuperAdmin';
      else if (name === 'privacy') pageName = this.$t('footer.legal.privacy');
      else if (name === 'terms') pageName = this.$t('footer.legal.terms');

      document.title = pageName ? `${pageName} | ${shop}` : shop;
    },
    handleGlobalRipple(e) {
      const btn = e.target.closest('button, .frm-submit-btn, .cal-confirm-btn, .edit-btn, .log-submit-btn, .reg-btn');
      if (btn && !btn.disabled && !btn.classList.contains('no-ripple')) {
        if (navigator.vibrate) navigator.vibrate(15);
        
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        const ripple = document.createElement('span');
        ripple.className = 'bu-ripple-element';
        ripple.style.left = `${x}px`;
        ripple.style.top = `${y}px`;
        
        const style = window.getComputedStyle(btn);
        if (style.position === 'static') btn.style.position = 'relative';
        btn.style.overflow = 'hidden';
        btn.appendChild(ripple);
        setTimeout(() => ripple.remove(), 600);
      }
    }
  },
  watch: {
    $route(to) {
      this.closeAllMenus();
      this.getShopName();
      this.updatePageTitle(to);
      window.scrollTo(0, 0); // Forces scroll reset for animations
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('click', this.handleGlobalRipple);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.checkCookieConsent();
    this.updatePageTitle();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('click', this.handleGlobalRipple);
    window.removeEventListener('scroll', this.handleScroll);
  },
  created() {
    this.setLanguage();
    this.getShopName();
  }
}
</script>

<style>
/* ── GLOBAL APP STYLES ─────────────────────────────────── */
html, body { scroll-behavior: smooth !important; -webkit-overflow-scrolling: touch !important; background-color: #04060e; color: #fff; }
::-webkit-scrollbar { width: 8px; height: 8px; }
::-webkit-scrollbar-track { background: #030712; }
::-webkit-scrollbar-thumb { background: #374151; border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: #4B5563; }

/* ── OVERLAYS ──────────────────────────────────────────── */
.noise-overlay { position: fixed; inset: 0; pointer-events: none; z-index: 9999; opacity: 0.04; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E"); }
.grain-overlay { position: fixed; inset: 0; pointer-events: none; z-index: 9998; opacity: 0.022; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E"); background-size: 180px; }

/* ── RIPPLE ────────────────────────────────────────────── */
.bu-ripple-element { position: absolute; border-radius: 50%; transform: scale(0); animation: bu-ripple-anim 0.6s cubic-bezier(0.4, 0, 0.2, 1); background: rgba(255, 255, 255, 0.25); pointer-events: none; width: 100px; height: 100px; margin-top: -50px; margin-left: -50px; z-index: 10; }
@keyframes bu-ripple-anim { to { transform: scale(4); opacity: 0; } }

/* ── CSS PAGE TRANSITIONS ──────────────────────────────── */
.page-fade-enter-active, .page-fade-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.page-fade-enter-from { opacity: 0; transform: translateY(12px) scale(0.98); }
.page-fade-leave-to { opacity: 0; transform: translateY(-12px) scale(0.98); }

/* ── GLOBAL MODAL OVERRIDES (FIXES Z-INDEX) ────────────── */
div[class*="modal-overlay"], div[class*="modal-backdrop"], div[class*="modal-mask"], .v-overlay__scrim, .modal-overlay, .bu-modal-overlay { z-index: 9999 !important; background-color: rgba(4, 6, 14, 0.6) !important; backdrop-filter: blur(12px) !important; -webkit-backdrop-filter: blur(12px) !important; }
div[class*="modal-content"], div[class*="modal-dialog"], .modal-container, .bottom-sheet { z-index: 10000 !important; }
</style>

<style scoped>
/* ── NEW PILL NAVBAR ───────────────────────────────────── */
.navbar-wrapper { position: absolute; top: 0; left: 0; width: 100%; z-index: 50; display: flex; justify-content: center; padding-top: 1.5rem; }
.navbar {
  position: fixed; width: 90%; max-width: 1200px; border-radius: 999px;
  background: rgba(15, 20, 32, 0.65); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.08); box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2); transition: all 0.35s ease;
}
.navbar--scrolled { background: rgba(10, 14, 24, 0.9); border-color: rgba(255, 255, 255, 0.15); box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5); transform: translateY(-0.5rem); }
.navbar__inner { padding: 0 1.5rem 0 2rem; height: 64px; display: flex; align-items: center; }

.navbar__logo { flex-shrink: 0; display: flex; align-items: center; opacity: 1; transition: opacity 0.2s; }
.navbar__logo:hover { opacity: 0.7; }
.navbar__logo-img { height: 28px; width: auto; display: block; filter: drop-shadow(0 2px 8px rgba(0,0,0,0.5)); }

.navbar__links { display: flex; align-items: center; list-style: none; padding: 0; margin: 0 0 0 2rem; gap: 0.5rem; flex: 1; }
.navbar__link {
  position: relative; display: block; padding: 0.4rem 0.8rem; font-size: 0.875rem; font-weight: 500; letter-spacing: 0.01em; color: rgba(230, 240, 250, 0.7); text-decoration: none; transition: color 0.35s ease; white-space: nowrap;
}
.navbar__link:hover, .navbar__link.router-link-active { color: #fff; }
.navbar__right { display: flex; align-items: center; gap: 0.8rem; margin-left: auto; }
.nav-divider { width: 1px; height: 20px; background: rgba(255,255,255,0.1); margin: 0 0.25rem; }

/* ── BUTTONS ───────────────────────────────────────────── */
.dash-btn { display: inline-flex; align-items: center; gap: 8px; border: none; border-radius: 12px; font-weight: 600; font-size: 0.85rem; cursor: pointer; transition: all 0.25s ease; font-family: inherit; }
.dash-btn:active { transform: scale(0.98); }
.btn-danger-outline { background: transparent; border: 1px solid rgba(239, 68, 68, 0.3); color: #f87171; }
.btn-danger-outline:hover { background: rgba(239, 68, 68, 0.1); border-color: rgba(239, 68, 68, 0.5); }

.btn-profile-icon {
  display: flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; border-radius: 50%;
  background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(230, 240, 250, 0.7); cursor: pointer; transition: all 0.35s ease;
  font-size: 1.1rem;
}
.btn-profile-icon:hover { background: rgba(255, 255, 255, 0.15); color: #fff; }

/* ── LANGUAGE DROPDOWN ─────────────────────────────────── */
.lang-btn { display: inline-flex; align-items: center; gap: 4px; height: 36px; padding: 0 0.8rem; background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255,255,255,0.08); border-radius: 50px; cursor: pointer; font-family: inherit; font-size: 0.88rem; color: rgba(230, 240, 250, 0.7); transition: all 0.35s ease; }
.lang-btn:hover { background: rgba(255,255,255,0.15); color: #fff; }
.lang-picker { position: relative; }
.lang-dropdown { position: absolute; top: calc(100% + 14px); right: 0; min-width: 130px; background: rgba(20, 25, 35, 0.98); border: 1px solid rgba(255, 255, 255, 0.15); box-shadow: 0 20px 60px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.3); backdrop-filter: blur(16px) saturate(180%); -webkit-backdrop-filter: blur(24px) saturate(150%); border-radius: 16px; padding: 0.5rem; }
.lang-option { display: flex; align-items: center; gap: 8px; width: 100%; padding: 0.48rem 0.65rem; border: none; background: transparent; border-radius: 10px; cursor: pointer; font-family: inherit; font-size: 0.83rem; font-weight: 500; color: rgba(230, 240, 250, 0.8); transition: all 0.14s; text-align: left; }
.lang-option:hover { background: rgba(255,255,255,0.08); color: #fff; }
.lang-option.is-active { color: #6499bb; }

/* ── FOOTER ────────────────────────────────────────────── */
.site-footer { background: #04060e; border-top: 1px solid rgba(255,255,255,0.04); }
.footer-inner { max-width: 1280px; margin: 0 auto; padding: 3.5rem 2.5rem 2.5rem; }
.footer-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 2rem; margin-bottom: 2.5rem; }
@media (max-width: 768px) { .footer-grid { grid-template-columns: 1fr 1fr; } }
@media (max-width: 480px) { .footer-grid { grid-template-columns: 1fr; } }
.footer-logo { height: 28px; width: auto; margin-bottom: 0.9rem; }
.footer-desc { font-size: 0.83rem; color: rgba(120,140,165,0.65); margin-bottom: 1rem; line-height: 1.6; }
.footer-heading { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(120,140,165,0.45); margin-bottom: 1rem; }
.footer-link { display: block; font-size: 0.85rem; color: rgba(140,165,190,0.55); text-decoration: none; margin-bottom: 0.6rem; transition: color 0.18s; }
.footer-link:hover { color: rgba(210,225,240,0.85); }
.footer-social { display: flex; gap: 1rem; margin-bottom: 1.2rem; }
.footer-social a { font-size: 1rem; color: rgba(120,145,170,0.5); text-decoration: none; transition: color 0.18s; }
.footer-social a:hover { color: rgba(200,220,235,0.8); }
.footer-bottom { border-top: 1px solid rgba(255,255,255,0.04); padding-top: 1.75rem; text-align: center; font-size: 0.78rem; color: rgba(100,120,145,0.45); }

/* ── COOKIE ────────────────────────────────────────────── */
.cookie-bar { position: fixed; bottom: 0; left: 0; right: 0; z-index: 100; padding: 1rem; pointer-events: none; }
.cookie-inner { max-width: 880px; margin: 0 auto; pointer-events: auto; background: rgba(8,12,24,0.96); border: 1px solid rgba(255,255,255,0.07); border-radius: 14px; padding: 1.1rem 1.4rem; backdrop-filter: blur(20px); display: flex; align-items: center; gap: 1.5rem; flex-wrap: wrap; }
.cookie-text { flex: 1; min-width: 200px; }
.cookie-text strong { display: block; font-size: 0.88rem; color: rgba(210,225,240,0.85); margin-bottom: 0.2rem; }
.cookie-text span { font-size: 0.82rem; color: rgba(140,165,190,0.6); }
.cookie-text a { color: #6499bb; text-decoration: none; }
.cookie-text a:hover { text-decoration: underline; }
.cookie-actions { display: flex; gap: 0.5rem; }
.cookie-btn { padding: 0.45rem 1rem; border-radius: 8px; font-size: 0.83rem; font-weight: 600; cursor: pointer; font-family: inherit; transition: all 0.18s; border: none; }
.cookie-btn--ghost { background: transparent; border: 1px solid rgba(255,255,255,0.09); color: rgba(160,185,210,0.65); }
.cookie-btn--ghost:hover { background: rgba(255,255,255,0.05); color: rgba(210,225,240,0.85); }
.cookie-btn--solid { background: #4a7fa5; color: #fff; }
.cookie-btn--solid:hover { background: #5a90b8; }

/* ── MOBILE BOTTOM NAV (B2B) ───────────────────────────── */
.desktop-hide { display: none; }
@media (max-width: 768px) {
  .desktop-hide { display: block; }
}
.mobile-bottom-nav { position: fixed; bottom: 0; left: 0; right: 0; z-index: 99; background: rgba(15, 20, 32, 0.85); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); border-top: 1px solid rgba(255, 255, 255, 0.08); padding-bottom: env(safe-area-inset-bottom); }
.mobile-bottom-nav__inner { display: flex; justify-content: space-around; align-items: center; height: 68px; padding: 0 0.5rem; }
.mobile-nav-btn { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; height: 100%; color: #64748b; font-size: 0.65rem; font-weight: 600; transition: all 0.2s ease; -webkit-tap-highlight-color: transparent; border: none; background: transparent; cursor: pointer; text-decoration: none; }
.mobile-nav-btn i { font-size: 1.15rem; margin-bottom: 2px; transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
.mobile-nav-btn.is-active { color: #6499bb; }
.mobile-nav-btn.is-active i { transform: translateY(-3px) scale(1.1); color: #8dbdd8; }
.mobile-nav-btn.logout-btn:active i { color: #f87171; }

/* ── RESPONSIVE HIDING (THE FIX) ───────────────────────── */
@media (max-width: 768px) { 
  .navbar__inner { padding: 0 1.25rem; } 
  .navbar { border-radius: 20px; width: 95%; } 
  /* Critical Fix: Completely hide the desktop links and buttons on mobile */
  .navbar__links { display: none !important; }
  .desktop-only { display: none !important; }
  .lang-text { display: none !important; }
}

/* Transitions */
.drop-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.drop-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.drop-enter-from, .drop-leave-to { opacity: 0; transform: translateY(-6px); }
</style>